import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Flashcard from './Flashcard';
import './FlashcardsPage.css';
import { getWordKey, getGroupId, persistSm2SrsStateToSupabase, srsSnapshotToWordFields } from '../utils/srs';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { useData } from '../contexts/DataContext';

const ReviewWeakWordsPage = () => {
  const { t, isRTL } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const { modules } = useData();

  // Get user session
  useEffect(() => {
    const getSession = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
    };
    getSession();
  }, []);

  const [currentModule, setCurrentModule] = useState(null);
  const [allWords, setAllWords] = useState([]); // All words in the module
  const [currentWordEntry, setCurrentWordEntry] = useState(null); // Currently displayed word
  const [isFlipped, setIsFlipped] = useState(false);
  const [preloadedSRSData, setPreloadedSRSData] = useState({});
  const preloadedSRSDataRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [learningLastUndo, setLearningLastUndo] = useState(null);
  const [learningUndoError, setLearningUndoError] = useState('');
  const initialCardSelectedRef = useRef(false);
  const selectionCounterRef = useRef(0);

  // Get the top 10 words with lowest learning_interval
  const getTop10WeakWords = useCallback((words, srsData, module) => {
    // Sort by learning_interval (lowest first), using most recent data from preloadedSRSData
    const sorted = [...words].sort((a, b) => {
      // Get the most recent learning_interval from preloadedSRSData, fallback to word object
      const groupIdA = getGroupId(module, a.groupIdx);
      const keyA = getWordKey(a.word, module.id, groupIdA);
      const srsA = srsData?.[keyA];
      const intervalA = srsA?.learning_interval ?? a.word.learning_interval ?? 0;
      
      const groupIdB = getGroupId(module, b.groupIdx);
      const keyB = getWordKey(b.word, module.id, groupIdB);
      const srsB = srsData?.[keyB];
      const intervalB = srsB?.learning_interval ?? b.word.learning_interval ?? 0;
      
      return intervalA - intervalB;
    });
    
    // Return top 10 (or all if less than 10)
    return sorted.slice(0, 10);
  }, []);

  // Select a random word from the top 10, excluding the current word
  const selectNextWord = useCallback(() => {
    if (allWords.length === 0 || !currentModule) return;
    
    const srsData = preloadedSRSDataRef.current || {};
    
    // Check if all words have learning_interval of 365 (highest possible)
    const allHaveMaxInterval = allWords.every(({ word, groupIdx }) => {
      const groupId = getGroupId(currentModule, groupIdx);
      const key = getWordKey(word, currentModule.id, groupId);
      const srs = srsData?.[key];
      const interval = srs?.learning_interval ?? word.learning_interval ?? 0;
      return interval === 365;
    });
    
    let candidates;
    
    if (allHaveMaxInterval) {
      // If all words are at max interval (365), randomly select from ALL words
      candidates = allWords;
    } else {
      // Otherwise, get top 10 words with lowest learning_interval
      const top10 = getTop10WeakWords(allWords, srsData, currentModule);
      if (top10.length === 0) return;
      candidates = top10;
    }
    
    // Filter out current word (unless there's only one word total)
    if (allWords.length > 1 && currentWordEntry) {
      candidates = candidates.filter(({ word, groupIdx }) => {
        return !(word.id === currentWordEntry.word.id && groupIdx === currentWordEntry.groupIdx);
      });
      
      // If filtering removed all candidates, allow current word if it's the only word
      if (candidates.length === 0) {
        if (allWords.length === 1) {
          candidates = allWords;
        } else {
          // Fallback: if we filtered everything out, use all words
          candidates = allWords.filter(({ word, groupIdx }) => {
            return !(word.id === currentWordEntry.word.id && groupIdx === currentWordEntry.groupIdx);
          });
          // If still empty (shouldn't happen), use all words
          if (candidates.length === 0) {
            candidates = allWords;
          }
        }
      }
    }
    
    // Select random from candidates
    const randomIndex = Math.floor(Math.random() * candidates.length);
    const selected = candidates[randomIndex];
    
    // Increment selection counter to force flashcard remount
    selectionCounterRef.current += 1;
    setCurrentWordEntry(selected);
    setIsFlipped(false);
  }, [allWords, currentWordEntry, getTop10WeakWords, currentModule]);

  useEffect(() => {
    const fetchModuleWords = async () => {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      
      if (!userId) {
        setLoading(false);
        return;
      }

      // First, try to find the module from dashboard data
      let currentMod = modules?.find(m => String(m.id) === String(id));

      // If not found in global state (e.g., on refresh or for classroom members), fetch it directly from Supabase
      if (!currentMod) {
        // Check if id is a UUID (contains dashes) or a position (number)
        const isUUID = typeof id === 'string' && id.includes('-');
        
        let query = supabase
          .from('modules_new')
          .select('*');
        
        if (isUUID) {
          // It's a module ID (classroom module) - RLS will handle access check
          query = query.eq('id', id);
        } else {
          // It's a position (private module)
          query = query.eq('position', Number(id))
                      .eq('user_id', userId);
        }
        
        const { data: moduleData, error: moduleError } = await query.single();

        if (moduleError) {
          console.error('Error fetching module:', moduleError);
          setLoading(false);
          return;
        }

        currentMod = moduleData;
      }

      if (!currentMod) {
        setLoading(false);
        return;
      }
      
      setCurrentModule(currentMod);

      try {
        // Fetch all word groups for this module
        const { data: groupsData, error: groupsError } = await supabase
          .from('word_groups')
          .select('*')
          .eq('module_id', currentMod.id)
          .order('position', { ascending: false });
        
        if (groupsError) {
          console.error('Error fetching word groups:', groupsError);
          setLoading(false);
          return;
        }

        // Fetch all words for all groups with pagination to get ALL words
        const groupIds = groupsData.map(g => g.id);
        let allWordsList = [];
        
        if (groupIds.length > 0) {
          let from = 0;
          const limit = 1000;
          let hasMore = true;
          
          while (hasMore) {
            const { data: wordsData, error: wordsError } = await supabase
              .from('words')
              .select(`
                id,
                group_id,
                from_text,
                to_text,
                position,
                created_at,
                updated_at,
                word_srs_data!left (
                  sm2_interval,
                  learning_interval,
                  sm2_ease,
                  learning_ease,
                  next_due,
                  apparent_tier,
                  history,
                  updated_at
                )
              `)
              .in('group_id', groupIds)
              .eq('word_srs_data.user_id', userId)
              .order('position', { ascending: true })
              .range(from, from + limit - 1);
            
            if (wordsError) {
              console.error('Error fetching words:', wordsError);
              setLoading(false);
              return;
            }
            
            if (wordsData && wordsData.length > 0) {
              // Merge SRS data into words
              const mergedWords = wordsData.map(word => {
                const srs = word.word_srs_data?.[0] || null;
                const sm2Ease = srs?.sm2_ease ?? 2.5;
                const learningEase = srs?.learning_ease ?? 2.5;
                return {
                  ...word,
                  word_srs_data: undefined,
                  sm2_interval: srs?.sm2_interval ?? 0,
                  learning_interval: srs?.learning_interval ?? 0,
                  sm2_ease: sm2Ease,
                  learning_ease: learningEase,
                  // Keep legacy ease alias mapped from SM2 ease for any callers that still read it
                  ease: sm2Ease,
                  next_due: srs?.next_due ?? 0,
                  apparent_tier: srs?.apparent_tier ?? 1,
                  history: srs?.history 
                    ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
                    : []
                };
              });
              allWordsList.push(...mergedWords);
              from += limit;
              hasMore = wordsData.length === limit;
            } else {
              hasMore = false;
            }
          }
        }

        // Organize words by group and deduplicate by word ID
        const wordsByGroupMap = new Map();
        groupsData.forEach((group, groupIdx) => {
          const groupWords = allWordsList.filter(word => word.group_id === group.id);
          groupWords.forEach(word => {
            const id = word.id;
            if (id && !wordsByGroupMap.has(id)) {
              wordsByGroupMap.set(id, { word, groupIdx });
            }
          });
        });
        const wordsByGroup = Array.from(wordsByGroupMap.values());

        if (wordsByGroup.length === 0) {
          setAllWords([]);
          setLoading(false);
          return;
        }

        // Preload SRS data (already merged from word_srs_data)
        const formattedSRSData = {};
        wordsByGroup.forEach(({ word, groupIdx }) => {
          const groupId = getGroupId(currentMod, groupIdx);
          const key = getWordKey(word, currentMod.id, groupId);

          const sm2Ease = word.sm2_ease ?? word.ease ?? 2.5;
          const learningEase = word.learning_ease ?? 2.5;

          formattedSRSData[key] = {
            sm2_interval: word.sm2_interval ?? 0,
            learning_interval: word.learning_interval ?? 0,
            sm2_ease: sm2Ease,
            learning_ease: learningEase,
            // Keep legacy ease alias mapped from SM2 ease for any callers that still read it
            ease: sm2Ease,
            nextDue: word.next_due ?? 0,
            history: Array.isArray(word.history) ? word.history : (word.history || []),
            apparentTier: word.apparent_tier ?? 1,
            lastUpdated: word.updated_at ? new Date(word.updated_at).getTime() : Date.now()
          };
        });

        setPreloadedSRSData(formattedSRSData);
        preloadedSRSDataRef.current = formattedSRSData;
        setAllWords(wordsByGroup);
      } catch (error) {
        console.error('Error loading words:', error);
      }
      
      setLoading(false);
    };

    fetchModuleWords();
  }, [id, modules]);

  // Select initial card when data is loaded
  useEffect(() => {
    if (!loading && allWords.length > 0 && currentModule && Object.keys(preloadedSRSData).length > 0 && !initialCardSelectedRef.current) {
      // Ensure we have SRS data before selecting
      // The ref should be populated by now since we set it when loading data
      selectNextWord();
      initialCardSelectedRef.current = true;
    }
  }, [loading, allWords, currentModule, preloadedSRSData, selectNextWord]);

  const handleSRSUpdate = useCallback((wordKey, updatedSRSData) => {
    // Update SRS data (both state and ref for immediate access)
    setPreloadedSRSData(prev => {
      const updated = {
        ...prev,
        [wordKey]: updatedSRSData
      };
      preloadedSRSDataRef.current = updated;
      return updated;
    });
    
    // Update the word in allWords array with new learning_interval
    setAllWords(prev => prev.map(({ word, groupIdx }) => {
      const groupId = getGroupId(currentModule, groupIdx);
      const key = getWordKey(word, currentModule.id, groupId);
      if (key === wordKey) {
        return {
          word: {
            ...word,
            learning_interval: updatedSRSData.learning_interval ?? word.learning_interval
          },
          groupIdx
        };
      }
      return { word, groupIdx };
    }));
  }, [currentModule]);

  // Handle next card selection after feedback
  const handleLearningNext = useCallback(() => {
    if (allWords.length === 0) return;
    selectNextWord();
  }, [allWords, selectNextWord]);

  const handleLearningRegisterUndo = useCallback((payload) => {
    setLearningLastUndo({
      ...payload,
      previousWordEntry: currentWordEntry
        ? JSON.parse(JSON.stringify(currentWordEntry))
        : null
    });
    setLearningUndoError('');
  }, [currentWordEntry]);

  const handleLearningGoBack = useCallback(async () => {
    if (!learningLastUndo || !session?.user?.id) return;
    const { wordKey, wordId, previousSrs, previousWordEntry } = learningLastUndo;
    if (!previousWordEntry) return;
    setLearningUndoError('');
    try {
      await persistSm2SrsStateToSupabase(wordId, session.user.id, previousSrs);
      const preloaded = {
        ...previousSrs,
        ease: previousSrs.sm2_ease ?? 2.5
      };
      const wf = srsSnapshotToWordFields(previousSrs);
      setPreloadedSRSData(prev => {
        const updated = { ...prev, [wordKey]: preloaded };
        preloadedSRSDataRef.current = updated;
        return updated;
      });
      setAllWords(prev =>
        prev.map(({ word, groupIdx }) =>
          word.id === wordId ? { word: { ...word, ...wf }, groupIdx } : { word, groupIdx }
        )
      );
      setCurrentWordEntry({
        ...previousWordEntry,
        word: { ...previousWordEntry.word, ...wf }
      });
      selectionCounterRef.current += 1;
      setIsFlipped(false);
      setLearningLastUndo(null);
    } catch (err) {
      console.error('Learning undo failed:', err);
      setLearningUndoError(t('errorUpdatingProgress') || 'Failed to update your progress. Please try again.');
    }
  }, [learningLastUndo, session?.user?.id, t]);

  useEffect(() => {
    let isProcessing = false;
    let lastKeyTime = 0;
    const DEBOUNCE_TIME = 300;
    let processingTimeout = null;
    
    const handleKeyDown = (e) => {
      if (isProcessing) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      if (e.repeat) {
        e.preventDefault();
        return;
      }
      
      const now = Date.now();
      if ((now - lastKeyTime) < DEBOUNCE_TIME) {
        e.preventDefault();
        return;
      }
      
      isProcessing = true;
      lastKeyTime = now;
      
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }
      
      try {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          const check = !isRTL ? 'ArrowRight' : 'ArrowLeft';
          const x = !isRTL ? 'ArrowLeft' : 'ArrowRight';

          if (e.key === check) {
            const checkButton = document.querySelector('.flashcard-feedback-btn.check');
            if (checkButton) {
              checkButton.click();
            }
          } else if (e.key === x) {
            const xButton = document.querySelector('.flashcard-feedback-btn.x');
            if (xButton) {
              xButton.click();
            }
          }
        } else if (e.code === 'Space') {
          e.preventDefault();
          setIsFlipped(prev => !prev);
        }
      } catch (error) {
        console.error('Error in review weak words key handler:', error);
      }
      
      processingTimeout = setTimeout(() => { 
        isProcessing = false; 
      }, DEBOUNCE_TIME);
    };
    
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }
    };
  }, [isRTL]);

  if (loading) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('loading') || 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  if (allWords.length === 0) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('noWords') || 'No words found.'}
          </div>
          <button onClick={() => navigate(`/module/${currentModule?.id || id}`)} className="btn btn-primary" style={{ marginTop: '1rem' }}>
            {t('returnToModule') || 'Return to Module'}
          </button>
        </div>
      </div>
    );
  }

  if (!currentWordEntry || !currentWordEntry.word) {
    return (
      <div className="flashcards-page-container">
        <div className="flashcard-view-container">
          <div className="flashcard-skeleton" style={{
            height: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: '#aaa'
          }}>
            {t('loading') || 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  const currentWord = currentWordEntry.word;
  const currentGroupIdx = currentWordEntry.groupIdx;

  return (
    <div className="flashcards-page-container">
      <button onClick={() => navigate(`/module/${currentModule?.id || id}`)} className="btn btn-secondary btn-back">
        {isRTL ? '→' : '←'} {t('exitFlashcards')}
      </button>
      <div className="flashcard-view-container">
        <Flashcard
          key={`${currentWord?.id || ''}-${selectionCounterRef.current}`}
          word={currentWord}
          module={currentModule}
          isFlipped={isFlipped}
          onFlip={() => setIsFlipped(prev => !prev)}
          moduleId={currentModule.id}
          groupIdx={currentGroupIdx}
          onFeedback={handleLearningNext}
          onSRSUpdate={handleSRSUpdate}
          userId={session?.user?.id}
          preloadedSRSData={preloadedSRSData}
          mode="learning"
          onLearningRegisterUndo={handleLearningRegisterUndo}
        />
        {learningLastUndo ? (
          <div
            className="learning-undo-active"
            style={{
              marginTop: '0.75rem',
              textAlign: 'center',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
          >
            <button
              type="button"
              onClick={handleLearningGoBack}
              className="btn btn-secondary"
              style={{
                width: '100%',
                maxWidth: '440px',
                fontWeight: 600,
                fontSize: '1rem',
                padding: '0.7rem 1rem'
              }}
            >
              {isRTL ? '→' : '←'} {t('sm2UndoLastCard')}
            </button>
            {learningUndoError ? (
              <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.9rem' }}>{learningUndoError}</div>
            ) : null}
          </div>
        ) : null}
        <div
          className="learning-mode-info"
          style={{
            marginTop: '0.75rem',
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: 'center'
          }}
        >
          {t('weakWordsModeInfo') || 'Reviewing your weakest words. Focus on these to strengthen your memory.'}
        </div>
      </div>
    </div>
  );
};

export default ReviewWeakWordsPage;
